import { z } from "zod";
import { MatchTypeEnum } from "../leads-v2/common";

export const ProjectStatusEnum = z.enum([
    "pending",
    "quoted",
    "in_progress",
    "submitted",
    "completed",
    "cancelled",
    "rejected",
]);
export type ProjectStatus = z.infer<typeof ProjectStatusEnum>;

export const ProjectTypeEnum = z.enum(["standard", "booking_call"]);
export type ProjectType = z.infer<typeof ProjectTypeEnum>;

export const QuoteStatusEnum = z.enum([
    "offered",
    "accepted",
    "rejected",
    "cancelled",
]);
export type QuoteStatus = z.infer<typeof QuoteStatusEnum>;

export const QuotePaymentStatusEnum = z.enum([
    "unpaid",
    "partial_paid",
    "paid",
    "partial_refund",
    "refund",
    "failed",
]);
export type QuotePaymentStatus = z.infer<typeof QuotePaymentStatusEnum>;

export const QuotePaymentModeEnum = z.enum(["internal", "external"]);
export type QuotePaymentMode = z.infer<typeof QuotePaymentModeEnum>;

export const QuoteIntervalEnum = z.enum(["one_time", "hourly", "hours_bank"]);
export type QuoteInterval = z.infer<typeof QuoteIntervalEnum>;

export const ProjectCurrencyCodeSchema = z.preprocess(
    (value) => (typeof value === "string" && value.trim() ? value : "USD"),
    z.string().trim().length(3).transform((value) => value.toUpperCase()),
);
export type ProjectCurrencyCode = z.infer<typeof ProjectCurrencyCodeSchema>;

export const ProjectParticipantGroupEnum = z.enum(["client", "expert"]);
export type ProjectParticipantGroup = z.infer<typeof ProjectParticipantGroupEnum>;

export const ProjectParticipantRoleEnum = z.enum([
    "owner",
    "manager",
    "contributor",
    "viewer",
]);
export type ProjectParticipantRole = z.infer<typeof ProjectParticipantRoleEnum>;

export const NoteKindEnum = z.enum(["personal_note", "group_note"]);
export type NoteKind = z.infer<typeof NoteKindEnum>;

export const AttachmentVisibilityEnum = z.enum([
    "all_participants",
    "same_participant_group",
    "sender_only",
]);
export type AttachmentVisibility = z.infer<typeof AttachmentVisibilityEnum>;

export const OrganizationRefSchema = z.object({
    id: z.number().nullish(),
    name: z.string().nullish(),
    org_type: z.string().nullish(),
});
export type OrganizationRef = z.infer<typeof OrganizationRefSchema>;

export const UserRefSchema = z.object({
    id: z.number(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    email: z.string().nullish(),
});
export type UserRef = z.infer<typeof UserRefSchema>;

export const LastMessageSummarySchema = z.object({
    id: z.string(),
    excerpt: z.string().nullish(),
    sent_at: z.string().nullish(),
    org: OrganizationRefSchema.nullish(),
    user: UserRefSchema.nullish(),
}).nullable();
export type LastMessageSummary = z.infer<typeof LastMessageSummarySchema>;

export const QuoteSummarySchema = z.object({
    id: z.number(),
    status: QuoteStatusEnum,
    payment_status: QuotePaymentStatusEnum.nullish(),
    amount_cents: z.number(),
    currency: ProjectCurrencyCodeSchema.default("USD"),
    interval: QuoteIntervalEnum,
    quantity: z.number(),
    delivery_after_days: z.number().nullish(),
    includes_weekends: z.boolean().nullish(),
    hours_bank_expires_after_days: z.number().nullish(),
    hours_bank_expiration_terms: z.string().nullish(),
    expires_at: z.string().nullish(),
    client_viewed_at: z.string().nullish(),
    payment_mode: QuotePaymentModeEnum.nullish(),
    external_payment_url: z.string().url().nullish(),
    payment_provider: z.string().nullish(),
    provider_payment_link_id: z.string().nullish(),
    provider_payment_intent_id: z.string().nullish(),
    provider_payment_url: z.string().url().nullish(),
    provider_invoice_url: z.string().url().nullish(),
    provider_payment_status: z.string().nullish(),
    payment_url: z.string().url().nullish(),
    invoice_url: z.string().url().nullish(),
    scope: z.string().nullish(),
    created_at: z.string().nullish(),
    status_changed_at: z.string().nullish(),
}).nullable();
export type QuoteSummary = z.infer<typeof QuoteSummarySchema>;

export const ProjectMediaResourceSchema = z.object({
    id: z.string(),
    key: z.string(),
    filename: z.string(),
    url: z.string().url().nullish(),
    cdn_url: z.string().url().nullish(),
    mime_type: z.string().nullish(),
    size: z.number().nullish(),
    formatted_size: z.string().nullish(),
    width: z.number().nullish(),
    height: z.number().nullish(),
    is_temporary: z.boolean(),
    collection: z.string(),
    created_at: z.string(),
    metadata: z.record(z.string(), z.unknown()).nullish(),
});
export type ProjectMediaResource = z.infer<typeof ProjectMediaResourceSchema>;

export const ProjectParticipantSummarySchema = z.object({
    org_id: z.number(),
    participant_group: ProjectParticipantGroupEnum,
    participant_role: ProjectParticipantRoleEnum,
    organization: OrganizationRefSchema,
});
export type ProjectParticipantSummary = z.infer<typeof ProjectParticipantSummarySchema>;

export const LeadPreferredExpertSchema = z.object({
    expert_id: z.number(),
    org_id: z.number(),
}).nullable();
export type LeadPreferredExpert = z.infer<typeof LeadPreferredExpertSchema>;

export const LeadStoreSummarySchema = z.object({
    id: z.number(),
    title: z.string().nullish(),
    url: z.string().nullish(),
}).nullable();
export type LeadStoreSummary = z.infer<typeof LeadStoreSummarySchema>;

export const LeadSummarySchema = z.object({
    id: z.number(),
    project_title: z.string().nullish(),
    project_brief: z.string().nullish(),
    urgency: z.string().nullish(),
    status: z.string().nullish(),
    budget_range: z.string().nullish(),
    created_at: z.string().nullish(),
    selected_expert: LeadPreferredExpertSchema.optional(),
    store: LeadStoreSummarySchema.optional(),
}).nullable();
export type LeadSummary = z.infer<typeof LeadSummarySchema>;

export const ProjectDetailLeadSchema = z.object({
    id: z.number(),
    project_title: z.string().nullish(),
    project_brief: z.string().nullish(),
    urgency: z.string().nullish(),
    matching_strategy: z.string().nullish(),
    match_type: MatchTypeEnum.nullish(),
    budget_range: z.string().nullish(),
    created_at: z.string().nullish(),
    matched_at: z.string().nullish(),
    selected_expert: LeadPreferredExpertSchema.optional(),
}).nullable();
export type ProjectDetailLead = z.infer<typeof ProjectDetailLeadSchema>;

export const ClientSummarySchema = z.object({
    id: z.number(),
    name: z.string().nullish(),
    owner_name: z.string().nullish(),
    owner_email: z.string().nullish(),
    organization: OrganizationRefSchema,
}).nullable();
export type ClientSummary = z.infer<typeof ClientSummarySchema>;

export const ProjectDetailClientSchema = z.object({
    name: z.string().nullish(),
    email: z.string().nullish(),
    avatar: z.string().nullish(),
}).nullable();
export type ProjectDetailClient = z.infer<typeof ProjectDetailClientSchema>;

export const ProjectExpertServiceSchema = z.object({
    name: z.string(),
    url: z.string(),
});

export const ProjectExpertPartnerSchema = z.object({
    name: z.string(),
    logo: z.string(),
    logo_media: ProjectMediaResourceSchema.nullish(),
    slug: z.string(),
});

export const ProjectExpertCustomerStorySchema = z.object({
    title: z.string(),
    slug: z.string(),
    images: z.array(z.string()),
    images_media: z.array(ProjectMediaResourceSchema).nullish(),
});

export const ProjectExpertLocationSchema = z.object({
    address: z.string(),
    countryAndCity: z.string(),
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
    verified: z.boolean(),
});

export const ProjectExpertRepresentativeSchema = z.object({
    picture: z.string().url().nullish(),
    picture_media: ProjectMediaResourceSchema.nullish(),
    name: z.string(),
    role: z.string(),
}).nullable();

export const ProjectExpertReviewsSchema = z.object({
    count: z.number(),
    average_rating: z.number(),
});

export const ExpertSummarySchema = z.object({
    id: z.number(),
    avatar: z.string().nullish(),
    name: z.string(),
    type: z.string(),
    listing_type: z.string(),
    partnerTier: z.string().nullish(),
    description: z.string().nullish(),
    slug: z.string().nullish(),
    isPro: z.boolean(),
    price: z.number(),
    overallReviewAverage: z.number(),
    overallReviewsCount: z.number(),
    services: z.array(ProjectExpertServiceSchema),
    partners: z.array(ProjectExpertPartnerSchema),
    customerStories: z.array(ProjectExpertCustomerStorySchema),
    location: ProjectExpertLocationSchema,
    languages: z.array(z.string()),
    representative: ProjectExpertRepresentativeSchema.optional(),
    profile_picture_media: ProjectMediaResourceSchema.nullish(),
    portfolio_media: z.array(ProjectMediaResourceSchema).nullish(),
    agency_type: z.string().nullable().optional(),
    agency_type_fields: z.record(z.string(), z.unknown()).optional(),
    title: z.string().optional(),
    organization_id: z.number().nullish(),
    role: z.string().nullish(),
    reviews: ProjectExpertReviewsSchema,
});
export type ExpertSummary = z.infer<typeof ExpertSummarySchema>;

export const ProjectDetailExpertSchema = z.object({
    name: z.string().nullish(),
    role: z.string().nullish(),
    type: z.string().nullish(),
    timezone: z.string().nullish(),
    location: ProjectExpertLocationSchema.nullish(),
    profile_url: z.string().url().nullish(),
    avatar: z.string().nullish(),
    overallReviewAverage: z.number(),
    overallReviewsCount: z.number(),
}).nullable();
export type ProjectDetailExpert = z.infer<typeof ProjectDetailExpertSchema>;

export const ProjectSummarySchema = z.object({
    id: z.number(),
    lead_id: z.number(),
    lead_match_id: z.number().nullish(),
    project_type: ProjectTypeEnum.default("standard"),
    title: z.string(),
    description: z.string().nullish(),
    project_value_range_id: z.number().nullish(),
    project_status: ProjectStatusEnum,
    status_changed_at: z.string().nullish(),
    last_message: LastMessageSummarySchema.optional(),
    latest_active_quote: QuoteSummarySchema.optional(),
    participants: z.array(ProjectParticipantSummarySchema),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
});
export type ProjectSummary = z.infer<typeof ProjectSummarySchema>;

export const ClientInboxProjectSchema = ProjectSummarySchema.extend({
    expert: ExpertSummarySchema.nullish(),
});
export type ClientInboxProject = z.infer<typeof ClientInboxProjectSchema>;

export const ExpertInboxProjectSchema = ProjectSummarySchema.extend({
    match_type: MatchTypeEnum.nullish(),
    client: ClientSummarySchema.optional(),
});
export type ExpertInboxProject = z.infer<typeof ExpertInboxProjectSchema>;

export const ClientInboxLeadGroupSchema = z.object({
    lead: LeadSummarySchema.optional(),
    projects: z.array(ClientInboxProjectSchema),
});
export type ClientInboxLeadGroup = z.infer<typeof ClientInboxLeadGroupSchema>;

export const ExposedQuoteSummarySchema = z.object({
    id: z.number(),
    status: QuoteStatusEnum,
    payment_status: QuotePaymentStatusEnum.nullish(),
    amount_cents: z.number(),
    currency: ProjectCurrencyCodeSchema.default("USD"),
    interval: QuoteIntervalEnum,
    quantity: z.number(),
    delivery_after_days: z.number().nullish(),
    includes_weekends: z.boolean().nullish(),
    hours_bank_expires_after_days: z.number().nullish(),
    hours_bank_expiration_terms: z.string().nullish(),
    expires_at: z.string().nullish(),
    client_viewed_at: z.string().nullish(),
    payment_mode: QuotePaymentModeEnum.nullish(),
    external_payment_url: z.string().url().nullish(),
    payment_provider: z.string().nullish(),
    provider_payment_link_id: z.string().nullish(),
    provider_payment_intent_id: z.string().nullish(),
    provider_payment_url: z.string().url().nullish(),
    provider_invoice_url: z.string().url().nullish(),
    provider_payment_status: z.string().nullish(),
    payment_url: z.string().url().nullish(),
    invoice_url: z.string().url().nullish(),
    scope: z.string().nullish(),
    created_at: z.string().nullish(),
    status_changed_at: z.string().nullish(),
});
export type ExposedQuoteSummary = z.infer<typeof ExposedQuoteSummarySchema>;

export const RawProjectRecordSchema = z.object({
    id: z.number(),
    lead_id: z.number(),
    lead_match_id: z.number().nullish(),
    project_type: ProjectTypeEnum.default("standard"),
    title: z.string(),
    description: z.string().nullish(),
    project_value_range_id: z.number().nullish(),
    project_status: ProjectStatusEnum,
    status_changed_at: z.string().nullish(),
    last_message_id: z.string().nullish(),
    last_message_at: z.string().nullish(),
    last_message_excerpt: z.string().nullish(),
    last_message_org_id: z.number().nullish(),
    last_message_user_id: z.number().nullish(),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
    deleted_at: z.string().nullish(),
});
export type RawProjectRecord = z.infer<typeof RawProjectRecordSchema>;

export const RawQuoteRecordSchema = z.object({
    id: z.number(),
    project_id: z.number(),
    offered_by_user_id: z.number().nullish(),
    offered_by_org_id: z.number().nullish(),
    offered_as_principal_id: z.number().nullish(),
    accepted_by_user_id: z.number().nullish(),
    accepted_by_org_id: z.number().nullish(),
    accepted_as_principal_id: z.number().nullish(),
    amount_cents: z.number(),
    currency: ProjectCurrencyCodeSchema.default("USD"),
    interval: QuoteIntervalEnum,
    quantity: z.number(),
    delivery_after_days: z.number().nullish(),
    includes_weekends: z.boolean().nullish(),
    hours_bank_expires_after_days: z.number().nullish(),
    hours_bank_expiration_terms: z.string().nullish(),
    expires_at: z.string().nullish(),
    client_viewed_at: z.string().nullish(),
    accepted_at: z.string().nullish(),
    status: QuoteStatusEnum,
    payment_status: QuotePaymentStatusEnum.nullish(),
    payment_mode: QuotePaymentModeEnum.nullish(),
    external_payment_url: z.string().url().nullish(),
    payment_provider: z.string().nullish(),
    provider_payment_link_id: z.string().nullish(),
    provider_payment_intent_id: z.string().nullish(),
    provider_payment_url: z.string().url().nullish(),
    provider_invoice_url: z.string().url().nullish(),
    provider_payment_status: z.string().nullish(),
    payment_url: z.string().url().nullish(),
    invoice_url: z.string().url().nullish(),
    scope: z.string().nullish(),
    status_changed_at: z.string().nullish(),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
    deleted_at: z.string().nullish(),
});
export type RawQuoteRecord = z.infer<typeof RawQuoteRecordSchema>;

export const ParticipantDisplayMapSchema = z.object({
    participants: z.array(z.object({
        org_id: z.number(),
        participant_group: ProjectParticipantGroupEnum,
        participant_role: ProjectParticipantRoleEnum,
        is_active: z.boolean(),
        expires_at: z.string().nullish(),
    })),
    organizations: z.array(z.object({
        id: z.number(),
        name: z.string(),
        org_type: z.string(),
        avatar: z.string().nullish(),
        profile_url: z.string().url().nullish(),
    })),
    users: z.array(z.object({
        id: z.number(),
        organization_ids: z.array(z.number()),
        display_name: z.string(),
        first_name: z.string().nullish(),
        last_name: z.string().nullish(),
        email: z.string().nullish(),
        avatar: z.string().nullish(),
        profile_url: z.string().url().nullish(),
    })),
});
export type ParticipantDisplayMap = z.infer<typeof ParticipantDisplayMapSchema>;

export const ProjectDetailStoreSchema = z.object({
    id: z.number(),
    title: z.string().nullish(),
    url: z.string().nullish(),
}).nullable();
export type ProjectDetailStore = z.infer<typeof ProjectDetailStoreSchema>;

export const ProjectDetailSchema = z.object({
    id: z.number(),
    project_type: ProjectTypeEnum.default("standard"),
    title: z.string(),
    description: z.string().nullish(),
    project_status: ProjectStatusEnum,
    latest_active_quote: QuoteSummarySchema.optional(),
    attachments: z.array(ProjectMediaResourceSchema),
    quotes: z.array(ExposedQuoteSummarySchema),
    expert: ProjectDetailExpertSchema.optional(),
    lead: ProjectDetailLeadSchema.optional(),
    client: ProjectDetailClientSchema.optional(),
    store: ProjectDetailStoreSchema.optional(),
    auto_review_requests_enabled: z.boolean().default(true),
});
export type ProjectDetail = z.infer<typeof ProjectDetailSchema>;

export const AdminProjectParticipantSchema = z.object({
    id: z.number().int().positive(),
    org_id: z.number(),
    participant_group: ProjectParticipantGroupEnum,
    participant_role: ProjectParticipantRoleEnum,
    expires_at: z.string().nullish(),
    organization: OrganizationRefSchema,
});
export type AdminProjectParticipant = z.infer<typeof AdminProjectParticipantSchema>;

export const AdminProjectActionsSchema = z.object({
    can_override_project_status: z.boolean(),
    can_override_quote_status: z.boolean(),
    can_override_quote_payment_status: z.boolean(),
    can_add_participant: z.boolean(),
    can_remove_participant: z.boolean(),
    can_unmatch: z.boolean(),
});
export type AdminProjectActions = z.infer<typeof AdminProjectActionsSchema>;

export const AdminProjectLeadSummarySchema = z.object({
    id: z.number(),
    project_title: z.string(),
    status: z.string(),
    matching_strategy: z.string(),
    matched_at: z.string().nullable(),
}).nullable();
export type AdminProjectLeadSummary = z.infer<typeof AdminProjectLeadSummarySchema>;

export const AdminProjectExpertSummarySchema = z.object({
    id: z.number(),
    org_id: z.number().nullable(),
    name: z.string(),
    slug: z.string(),
    type: z.enum(["freelancer", "agency"]),
    email: z.string().nullable(),
}).nullable();
export type AdminProjectExpertSummary = z.infer<typeof AdminProjectExpertSummarySchema>;

export const AdminProjectSummarySchema = z.object({
    id: z.number(),
    lead_id: z.number(),
    lead_match_id: z.number().nullish(),
    project_type: ProjectTypeEnum.default("standard"),
    title: z.string(),
    description: z.string().nullish(),
    project_status: ProjectStatusEnum,
    status_changed_at: z.string().nullish(),
    created_at: z.string().nullish(),
    lead: AdminProjectLeadSummarySchema,
    client: ClientSummarySchema,
    expert: AdminProjectExpertSummarySchema,
    latest_active_quote: QuoteSummarySchema,
    participants: z.array(AdminProjectParticipantSchema),
    admin_actions: AdminProjectActionsSchema,
});
export type AdminProjectSummary = z.infer<typeof AdminProjectSummarySchema>;

export const AdminProjectEventSchema = z.object({
    id: z.number(),
    event_type: z.string(),
    actor_user_id: z.number().nullish(),
    actor_org_id: z.number().nullish(),
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
    created_at: z.string().nullish(),
});
export type AdminProjectEvent = z.infer<typeof AdminProjectEventSchema>;

export const AdminQuoteEventSchema = AdminProjectEventSchema;
export type AdminQuoteEvent = z.infer<typeof AdminQuoteEventSchema>;

export const AdminProjectQuoteSchema = z.object({
    id: z.number(),
    status: QuoteStatusEnum,
    payment_status: QuotePaymentStatusEnum.nullish(),
    amount_cents: z.number(),
    currency: ProjectCurrencyCodeSchema.default("USD"),
    interval: QuoteIntervalEnum,
    quantity: z.number(),
    delivery_after_days: z.number().nullish(),
    includes_weekends: z.boolean().nullish(),
    hours_bank_expires_after_days: z.number().nullish(),
    hours_bank_expiration_terms: z.string().nullish(),
    expires_at: z.string().nullish(),
    client_viewed_at: z.string().nullish(),
    payment_mode: QuotePaymentModeEnum.nullish(),
    external_payment_url: z.string().url().nullish(),
    payment_provider: z.string().nullish(),
    provider_payment_link_id: z.string().nullish(),
    provider_payment_intent_id: z.string().nullish(),
    provider_payment_url: z.string().url().nullish(),
    provider_invoice_url: z.string().url().nullish(),
    provider_payment_status: z.string().nullish(),
    payment_url: z.string().url().nullish(),
    invoice_url: z.string().url().nullish(),
    scope: z.string().nullish(),
    accepted_at: z.string().nullish(),
    status_changed_at: z.string().nullish(),
    created_at: z.string().nullish(),
    events: z.array(AdminQuoteEventSchema),
});
export type AdminProjectQuote = z.infer<typeof AdminProjectQuoteSchema>;

export const AdminProjectRejectedBySchema = z.object({
    id: z.number(),
    name: z.string().nullable(),
    email: z.string().nullable(),
}).nullable();
export type AdminProjectRejectedBy = z.infer<typeof AdminProjectRejectedBySchema>;

export const AdminProjectDetailSchema = AdminProjectSummarySchema.extend({
    store: ProjectDetailStoreSchema,
    quotes: z.array(AdminProjectQuoteSchema),
    attachments: z.array(ProjectMediaResourceSchema),
    events: z.array(AdminProjectEventSchema),
    rejection_reason: z.string().nullish(),
    rejected_at: z.string().nullish(),
    rejected_by: AdminProjectRejectedBySchema.optional(),
});
export type AdminProjectDetail = z.infer<typeof AdminProjectDetailSchema>;
